import { useClientPortalAPI, useFormikSubmit } from "@certn/api";
import { components } from "@certn/api/generated-schemas/client-portal";
import { showToast } from "@certn/cascadia";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { array, object, string } from "yup";

import { Question } from "../types";

export function useData(id?: string) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { mutate } = useClientPortalAPI(
    `/api/internal/client-portal/questionnaire-templates/`
  );
  const {
    data: questions,
    isLoading: isLoadingQuestions,
    error: questionsError,
  } = useClientPortalAPI(
    `/api/internal/client-portal/questionnaire-templates/default-questions/`
  );
  const { data: templateToEdit } = useClientPortalAPI(
    id ? `/api/internal/client-portal/questionnaire-templates/${id}/` : null
  );
  const { submit } = useFormikSubmit(
    id
      ? `/api/internal/client-portal/questionnaire-templates/${id}/update/`
      : "/api/internal/client-portal/questionnaire-templates/create/",
    {
      onSuccess: () => {
        showToast(
          id
            ? t("Questionnaire template saved successfully")
            : t("Questionnaire template created successfully")
        );
        mutate();
        navigate(`/settings/questionnaire-templates`);
      },
      showToastOnError: true,
    }
  );
  const getQuestions = (
    type: components["schemas"]["DefaultQuestionsResponseTypeEnum"],
    questions?: components["schemas"]["DefaultQuestionsResponse"][]
  ) => {
    if (questions) {
      return questions
        .filter((question) => question.type === type)
        .map((question) => {
          if (id && templateToEdit) {
            const foundQuestion = Object.keys(
              templateToEdit.input_claims_with_prompts
            ).find((key) => key === question.claim_name);

            return {
              selected: !!foundQuestion,
              claim_name: question.claim_name,
              prompt: foundQuestion
                ? templateToEdit.input_claims_with_prompts[foundQuestion] || ""
                : question.prompt,
              type: question.type,
              question: question.question,
            };
          }
          return {
            selected: false,
            claim_name: question.claim_name,
            prompt: question.prompt,
            type: question.type,
            question: question.question,
          };
        });
    }
  };

  const schema = object({
    name: string().required().label(t("Template name")),
    questions: array()
      .test(
        "at-least-one-question",
        t("At least one question is required"),
        (value, testContext) => {
          return (
            value?.some((d: Question) => d.selected) ||
            testContext.parent.documents.some((d: Question) => d.selected)
          );
        }
      )
      .test(
        "at-least-one-question-has-prompt",
        t("At least one question must have a prompt"),
        (value, testContext) => {
          return (
            value?.some((d: Question) => d.selected && d.prompt) ||
            testContext.parent.documents.some(
              (d: Question) => d.selected && d.prompt
            )
          );
        }
      ),
  });

  const initialValues = {
    name: templateToEdit?.name || "",
    is_disclaimer_accepted: false,
    template_type: "TENANCY",
    input_claims_with_prompts: {},
    questions: getQuestions("QUESTION", questions),
    documents: getQuestions("DOCUMENT_UPLOAD", questions),
  };

  const getSelectedQuestions = (
    questions?: Question[],
    documents?: Question[]
  ) => {
    const selectedQuestions: Record<string, string> = {};
    questions
      ?.filter((question) => question.selected)
      .forEach((question) => {
        selectedQuestions[question.claim_name] = question.prompt;
      });

    const selectedDocuments: Record<string, string> = {};
    documents
      ?.filter((document) => document.selected)
      .forEach((document) => {
        selectedDocuments[document.claim_name] = document.prompt;
      });

    return { ...selectedQuestions, ...selectedDocuments };
  };

  return {
    isLoadingQuestions,
    questionsError,
    initialValues,
    questions,
    schema,
    getQuestions,
    getSelectedQuestions,
    submit,
    mutate,
  };
}
